import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Cookie consent banner for the public marketing site.
 *
 * Ties to the Privacy Policy's "Cookies & Tracking" section, which distinguishes
 * Essential (always on), Analytics, and Preference cookies. The banner lets a
 * visitor accept all non-essential categories or keep essential-only, and
 * remembers the choice in localStorage so it never nags a returning visitor.
 *
 * TIMING (compliance-relevant): visibility is computed SYNCHRONOUSLY in the
 * initial render — the banner is part of the very first React paint, with no
 * post-mount reveal gap. Just as important, the pre-JS window contains nothing
 * that needs consent: no third-party scripts, no cookies, and fonts are
 * self-hosted (see index.html). "Banner immediate + nothing fires before it"
 * is the pair that keeps this surface safe.
 *
 * Reading the stored value elsewhere: use readCookieConsent() below.
 *   'all'       → analytics + preference OK
 *   'essential' → essential only
 *   null        → no choice yet (treat as essential-only)
 *
 * GDPR Art. 7(3) requires withdrawing consent to be as easy as giving it, so the
 * footer's "Cookie Preferences" link calls openCookiePreferences() to bring the
 * banner back for a visitor who already chose.
 */
const CONSENT_KEY = 'cookie.consent';
const REOPEN_EVENT = 'cookie-consent:open';
/** Broadcast on every choice. detail = 'all' | 'essential'.
 *  Consent is not a page-load-time constant: a visitor who accepts analytics
 *  AFTER landing has already had signals (their referrer) discarded, and the
 *  only way to honour the choice they just made is for the modules that gated
 *  on consent to hear about it. lib/attribution.ts listens for this. */
const CHANGED_EVENT = 'cookie-consent:changed';

export type CookieConsent = 'all' | 'essential' | null;

export function readCookieConsent(): CookieConsent {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'all' || v === 'essential' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Global Privacy Control (CPRA-recognized opt-out signal). When the browser
 * broadcasts GPC we treat the visitor as essential-only regardless of any
 * stored "all" — the signal is a standing objection and honoring it is the
 * future-proof reading of California law (and costs us nothing: only
 * analytics enrichment is gated).
 */
export function gpcActive(): boolean {
  try {
    return (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

/** True only when the visitor affirmatively accepted non-essential cookies
 *  AND their browser is not broadcasting a GPC opt-out. */
export function hasAnalyticsConsent(): boolean {
  return readCookieConsent() === 'all' && !gpcActive();
}

/** Re-show the banner so a visitor can change (or withdraw) an earlier choice. */
export function openCookiePreferences() {
  window.dispatchEvent(new Event(REOPEN_EVENT));
}

export function CookieBanner() {
  // Synchronous initial state: no useEffect delay, no post-mount reveal gap.
  // Returning visitors with a stored choice never see a flash (the initializer
  // reads localStorage before the first paint).
  const [visible, setVisible] = useState(() => !readCookieConsent());

  // Listener only — it never gates the FIRST paint (that stays synchronous
  // above); it just lets the footer link bring the banner back later.
  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_EVENT, reopen);
  }, []);

  function choose(value: 'all' | 'essential') {
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch {
      /* ignore — private mode; banner still dismisses for the session */
    }
    // Storage is written BEFORE the broadcast so any listener that re-reads
    // consent (rather than trusting detail) sees the new value.
    try {
      window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: value }));
    } catch {
      /* CustomEvent unavailable — the choice is still stored */
    }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface-1/95 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-8">
        <p className="text-[13px] leading-relaxed text-ink-muted">
          We use cookies to run the site and, with your consent, to understand usage and remember
          your preferences. See our{' '}
          <Link to="/privacy" className="text-primary-bright underline underline-offset-2 hover:text-ink">
            Privacy Policy
          </Link>{' '}
          for details.
        </p>
        <div className="flex shrink-0 items-center gap-2.5">
          <button
            type="button"
            onClick={() => choose('essential')}
            className="rounded-full px-4 py-1.5 text-[13px] text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => choose('all')}
            className="rounded-full border border-primary bg-primary px-4 py-1.5 text-[13px] font-semibold text-white transition-all hover:brightness-110"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
